import axios from "axios";
import { useCanvasStore } from "@/store/zustand/canvasStore";

export const getMachineForCanvas = async (
  canvasId: string,
  supabaseClient: any
) => {
  if (!supabaseClient) {
    return null;
  }

  try {
    const { canvasToMachineMapping, setCanvasToMachineMapping } =
      useCanvasStore.getState();

    let machineForCanvas = canvasToMachineMapping[canvasId];

    if (!machineForCanvas) {
      const { data, error } = await supabaseClient
        .from("canvas_machine_mapping")
        .select(
          `
          machine:deployment_machines (
            *
          )`
        )
        .eq("canvas_id", canvasId)
        .single();

      if (error) {
        console.error("Error fetching canvas mapping:", error);
        return null;
      }

      if (data?.machine) {
        machineForCanvas = data.machine;
        setCanvasToMachineMapping(canvasId, machineForCanvas);
      }
    }

    return machineForCanvas;
  } catch (e) {
    console.error("Error fetching canvas mapping:", e);
    return null;
  }
};

export const checkRedisHealth = async (
  canvasId: string,
  supabaseClient: any
): Promise<{
  confirmedRedisDown: boolean;
  anyCheckFailed: boolean;
}> => {
  if (!supabaseClient) {
    return {
      confirmedRedisDown: false,
      anyCheckFailed: false,
    };
  }

  const checkRedisIsUp = async (
    canvasId: string,
    supabaseClient: any
  ): Promise<boolean> => {
    try {
      if (import.meta.env.VITE_ENVIRONMENT !== "PROD") {
        const redisHealthUrl = `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/redis-health`;
        const res = await axios.get(redisHealthUrl, {});
        return res.status === 200;
      }

      const machineForCanvas = await getMachineForCanvas(
        canvasId,
        supabaseClient
      );
      if (!machineForCanvas) {
        return true;
      }

      const redisHealthUrl = `${machineForCanvas.url}/redis-health`;
      const res = await axios.get(redisHealthUrl, {});
      return res.status === 200;
    } catch (e) {
      console.error("Error checking Redis health:", e);
      return false;
    }
  };

  let redisIsUp = await checkRedisIsUp(canvasId, supabaseClient);
  let attempts = 1;

  let anyCheckFailed = !redisIsUp;

  // Check 3 times until it's up with a 1 second delay between each check
  while (attempts < 3 && !redisIsUp) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    redisIsUp = await checkRedisIsUp(canvasId, supabaseClient);

    anyCheckFailed = anyCheckFailed || !redisIsUp;
    attempts++;
  }

  return {
    confirmedRedisDown: !redisIsUp,
    anyCheckFailed,
  };
};
